/** Public site URL for sitemap, canonical tags, and Open Graph (no trailing slash) */
export const siteUrl =
  process.env.VUE_APP_SITE_URL || "https://virtualassistanthouse.com";

// Update brand.name when your public brand name is finalized
export const brand = {
  name: "Virtual Assistant House",
  tagline: "One main skill, plus three more they've got covered",
  description:
    "VA House gives you one agent with a main skill and three secondary skills across admin, customer support, and call handling — not three separate hires. Managed, vetted, and backed by a team for reliability.",
};

export const logo = {
  imageUrl: "/images/va-bare.png",
  alt: "Virtual Assistant House logo",
  initials: "VH",
};

/** Free trial promo — edit copy here; shown in header, hero, pricing, and CTAs */
export const premiumTrial = {
  enabled: true,
  badge: "Limited time",
  headline: "Free trial of our Premium Virtual Assistance",
  description:
    "Experience fully managed Premium VA support — vetted Filipino talent, expert matching, backup coverage, and hands-on account management — before you commit to a plan.",
  ctaLabel: "Start your free trial",
  ctaTo: "/contact",
  footnote: "For new clients on eligible plans. Terms apply.",
};

/** Tidio live chat — set VUE_APP_TIDIO_PUBLIC_KEY in .env to enable */
export const tidio = {
  hideOnRoutes: ["/contact"],
};

export const contact = {
  email: "hello@virtualassistanthouse.com",
  calendlyUrl: "https://calendly.com/vahousecorp-marketing/30min",
  calendlyLabel: "Book a Free Consultation",
  /** Digits only with country code (no + or spaces) — used for wa.me links */
  whatsapp: {
    number: "639970620726",
    display: "+63 997 062 0726",
    label: "Chat on WhatsApp",
  },
  // v2 = "I'm not a robot" checkbox | v3 = invisible score on submit
  recaptchaVersion: (
    process.env.VUE_APP_RECAPTCHA_VERSION || "v3"
  ).toLowerCase(),
  // Replace with your key via VUE_APP_RECAPTCHA_SITE_KEY in .env
  // v2 test key (always passes): 6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI
  recaptchaSiteKey:
    process.env.VUE_APP_RECAPTCHA_SITE_KEY ||
    "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI",
};

/** Official social profiles — update hrefs if handles change */
export const socialLinks = [
  {
    id: "facebook",
    label: "Facebook",
    href: "https://www.facebook.com/vahousecorp",
  },
  {
    id: "instagram",
    label: "Instagram",
    href: "https://www.instagram.com/vahousecorp/",
  },
  {
    id: "tiktok",
    label: "TikTok",
    href: "https://www.tiktok.com/@vahousecorp",
  },
  {
    id: "linkedin",
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/vahousecorporation",
  },
];

export const navLinks = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "How It Works", to: "/how-it-works" },
  { label: "Industries", to: "/#industries" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

/** Primary homepage CTAs */
export const homeCtas = {
  findMyVa: { label: "Find My VA", to: "/contact" },
  bookConsultation: {
    label: "Book a Free Consultation",
    href: contact.calendlyUrl,
  },
  seeHowItWorks: { label: "See How It Works", to: "/#process" },
  exploreServices: { label: "Explore Our Services", to: "/services" },
  tellUsWhoYouNeed: { label: "Tell Us Who You Need", to: "/contact" },
  discussStaffing: {
    label: "Discuss Your Staffing Needs",
    href: contact.calendlyUrl,
  },
};

export const homeHero = {
  eyebrow: "More Than a Virtual Assistant",
  headline: "One Main Skill.",
  headlineAccent: "Three More They've Got Covered.",
  subheadline:
    "Every VA House agent has one core specialty — admin, customer support, or calls — plus working proficiency in the other two. So you get an expert where it matters most, and coverage everywhere else, instead of three separate hires.",
  panelKicker: "The VA House model",
  highlights: [
    {
      title: "Matched by main skill",
      description:
        "We pair you with an expert in the discipline eating most of your week.",
    },
    {
      title: "Three secondary skills",
      description:
        "Working proficiency in the other two — overflow coverage without a second hire.",
    },
    {
      title: "Team-backed reliability",
      description:
        "Supervisor, trained backup, and account manager standing behind your agent.",
    },
  ],
};

export const homeProblems = {
  eyebrow: "The problem",
  title: "Hiring Three People to Do One Job Is Expensive — and Unnecessary",
  paragraphs: [
    "Growing businesses usually end up needing an admin assistant, a customer support rep, and someone to handle calls. Hired separately, that's three job posts, three onboarding processes, three people to manage, and three salaries — even when none of them is busy full-time.",
    "Marketplaces like Upwork don't fix this. You still do the vetting, the training, and the babysitting — and if your freelancer goes quiet, gets sick, or quits, there's no one behind them to catch it. You're back to square one.",
  ],
};

export const homeDifferentiator = {
  eyebrow: "The difference",
  title: "Not Just a VA — A Multi-Skilled Agent",
  intro:
    "Every VA House agent is matched to you by their main skill — the discipline they're strongest in — plus proficiency in the other two:",
  skills: [
    {
      id: "admin",
      title: "Administrative support",
      description: "Inbox, calendar, data entry, research, scheduling",
      to: "/services#general-va",
    },
    {
      id: "customer-support",
      title: "Customer support",
      description: "Tickets, live chat, email support, order/account issues",
      to: "/services#call-center",
    },
    {
      id: "call-handling",
      title: "Call handling",
      description: "Inbound/outbound calls, appointment setting, follow-ups",
      to: "/services#callers",
    },
  ],
  example:
    "So if your biggest need is customer support, you get an agent whose main skill is customer support — who can also competently handle your admin and calls when things get busy, instead of needing a second or third hire for that overflow.",
  callout:
    "One agent, one main skill they've mastered — plus the range to handle what comes up around it.",
  supporting:
    "No single hire can be an expert at everything. What your agent gives you is real strength where you need it most, and reliable coverage everywhere else.",
};

export const homeTeamBehind = {
  eyebrow: "Reliability",
  title: "One Skilled Agent — Never Working Without Backup",
  intro:
    "Your multi-skilled agent is your day-to-day point of contact. Behind them:",
  roles: [
    {
      id: "supervisor",
      title: "A supervisor",
      description:
        "Reviews quality and keeps performance consistent",
    },
    {
      id: "backup",
      title: "A trained backup",
      description:
        "Already knows your account and can step in without missing a beat",
    },
    {
      id: "account-manager",
      title: "An account manager",
      description:
        "Checks in, handles issues, and makes sure the relationship keeps working for you",
    },
  ],
  supporting:
    "You get one skilled agent to work with — and the reliability of a team standing behind them.",
};

export const homeComparison = {
  eyebrow: "Compare",
  title: "VA House vs. the Alternatives",
  columns: ["VA House", "Upwork / Marketplaces", "Single-Task VA Agencies"],
  rows: [
    {
      feature: "Skill coverage",
      values: [
        "1 main skill (expert) + 3 secondary skills (proficient)",
        "Whatever that freelancer offers",
        "One lane only, no secondary coverage",
      ],
    },
    {
      feature: "Cost efficiency",
      values: [
        "One hire covers overflow across roles",
        "Pay per freelancer, per skill",
        "Pay per specialist, per skill",
      ],
    },
    {
      feature: "Vetting & training",
      values: ["Done for you", "You do it yourself", "Done for you"],
    },
    {
      feature: "Management overhead",
      values: [
        "Low — one point of contact",
        "High — you manage each freelancer",
        "Medium — still need multiple vendors",
      ],
    },
    {
      feature: "Backup if your person is out",
      values: [
        "Team-backed — trained backup steps in",
        "None — you're stuck",
        "Varies by agency",
      ],
    },
    {
      feature: "Replacement if it's not a fit",
      values: [
        "Guaranteed replacement",
        "Find and re-vet a new freelancer",
        "Guaranteed, but only within one skill lane",
      ],
    },
    {
      feature: "Onboarding time",
      values: [
        "Fast — SOPs built in",
        "Varies widely",
        "Fast, but narrow scope",
      ],
    },
  ],
};

export const homeHowItWorks = {
  eyebrow: "How it works",
  title: "Getting Started Takes Three Steps",
  steps: [
    {
      step: 1,
      title: "Free Consultation",
      summary:
        "Tell us which task is eating the most of your week — that becomes your agent's main skill match.",
    },
    {
      step: 2,
      title: "Matched Agent",
      summary:
        "We match you with a pre-vetted agent whose main skill fits your biggest need, plus secondary proficiency in the other two.",
    },
    {
      step: 3,
      title: "Pilot Week",
      summary:
        "Your agent starts on a trial basis so you can see the main-skill-plus-coverage model in action before committing.",
    },
  ],
};

export const homeTrust = {
  eyebrow: "Trust & guarantee",
  title: "Backed by a No-Risk Guarantee",
  items: [
    "Pre-vetted agents, matched by their main skill — not generalists spread thin",
    "Working proficiency in 3 secondary skills for overflow and coverage",
    "Supported by a dedicated team, not working solo",
    "Dedicated account manager for every client",
    "Trained backup coverage if your agent is ever unavailable",
    "Free replacement if your agent isn't the right fit",
    "Flexible monthly plans — no long-term lock-in",
  ],
};

export const homeServiceOfferings = [
  {
    id: "virtual-assistants",
    title: "Virtual Assistants",
    description:
      "Admin tasks, scheduling, inbox management, research, and more.",
    to: "/services#general-va",
    imageKey: "general-va",
  },
  {
    id: "customer-support",
    title: "Customer Support",
    description: "Voice, email, chat, and customer service.",
    to: "/services#call-center",
    imageKey: "call-center",
  },
  {
    id: "sales-support",
    title: "Sales Support",
    description: "Lead generation, appointment setting, and outbound support.",
    to: "/services#sales",
    imageKey: "sales",
  },
  {
    id: "back-office",
    title: "Back-Office Support",
    description: "Data entry, processing, reporting, and administrative work.",
    to: "/services#data-analyst",
    imageKey: "general-va",
  },
  {
    id: "marketing-support",
    title: "Marketing Support",
    description:
      "Social media, graphic design, video editing, and content assistance.",
    to: "/services#social-media-manager",
    imageKey: "social-media-manager",
  },
  {
    id: "dedicated-teams",
    title: "Dedicated Teams",
    description:
      "Build a customized offshore team based on your business requirements.",
    to: "/contact",
    imageKey: "call-center",
  },
];

export const homeRoleGroups = [
  {
    heading: "Administrative",
    roles: [
      { title: "General VA", to: "/services#general-va" },
      { title: "Executive Assistant", to: "/services#executive-assistant" },
      { title: "Data Entry Specialist", to: "/services#data-analyst" },
      { title: "Admin Assistant", to: "/services#general-va" },
    ],
  },
  {
    heading: "Customer Support",
    roles: [
      { title: "Customer Service Rep", to: "/services#call-center" },
      { title: "Email Support", to: "/services#call-center" },
      { title: "Chat Support", to: "/services#call-center" },
      { title: "Technical Support", to: "/services#it-support" },
    ],
  },
  {
    heading: "Sales & Marketing",
    roles: [
      { title: "Appointment Setter", to: "/services#callers" },
      { title: "Lead Generation Specialist", to: "/services#sales" },
      { title: "Social Media VA", to: "/services#social-media-manager" },
      { title: "Marketing Assistant", to: "/services#social-media-manager" },
    ],
  },
];

export const homeIndustries = {
  eyebrow: "Who it's for",
  title: "Built for Businesses That Need More Than One Skill",
  intro:
    "Some businesses only need admin help. Most need admin, customer communication, and phone/call coverage — all at once. That's exactly where a multi-skilled agent earns their keep:",
  items: [
    {
      id: "real-estate",
      name: "Real estate teams",
      description:
        "Lead follow-up calls, MLS/listing admin, transaction coordination, and client scheduling. Speed matters here: agents who respond to leads fast close more deals, and a multi-skilled agent means follow-up never waits on you.",
    },
    {
      id: "local-services",
      name: "Health, fitness & local services",
      description:
        "Appointment scheduling calls, client onboarding, membership/booking admin, and day-to-day client support in one hire instead of a front-desk person plus a support rep.",
    },
    {
      id: "ecommerce",
      name: "E-commerce brands",
      description:
        "Customer support tickets and order/returns handling as the core skill, backed by admin (listings, inventory updates) and call support for order issues that need a human voice.",
    },
    {
      id: "agencies-coaching",
      name: "Marketing agencies, coaching & consulting",
      description:
        "Top VA users across the board — high-volume, recurring, tool-based work like content calendars, CRM updates, and client onboarding that one multi-skilled agent can run end to end.",
    },
    {
      id: "saas",
      name: "IT, SaaS & startups",
      description:
        "Fast-growing teams with lower call volume. VAs here handle documentation, ticket triage, and internal ops more than phone-based work — with secondary coverage when client communication spikes.",
    },
    {
      id: "finance-legal",
      name: "Finance, bookkeeping & legal",
      description:
        "Steady, sticky usage that is more specialized and compliance-sensitive. These buyers typically want a VA with bookkeeping or legal-admin expertise specifically, not a generalist.",
      to: "/services#bookkeeper",
    },
  ],
};

export const homeScaleTeam = {
  eyebrow: "Scale",
  title: "Need More Than One Person? Build a Team, Not Just a Hire.",
  intro:
    "Some businesses need one flexible agent. Others need volume — a group of dedicated chat support reps handling simultaneous conversations, or a team of callers running outbound follow-up at scale. We support both.",
  tiers: [
    {
      id: "one-agent",
      name: "One Agent",
      size: "1 person",
      description:
        "A single multi-skilled agent covering admin, support, and calls. Best for solo founders and small teams.",
    },
    {
      id: "pod",
      name: "Small Pod",
      size: "2–4 agents",
      description:
        "A coordinated group, e.g., a dedicated chat support agent plus a dedicated caller, working together under one account manager. Best for growing teams with rising volume in one or two areas.",
    },
    {
      id: "full-team",
      name: "Full Team",
      size: "High-volume coverage",
      description:
        "Multiple chat agents and/or callers running simultaneously for call-center-level or high-volume support coverage. Best for businesses that have outgrown a single point of contact.",
    },
  ],
  supporting:
    "You're not locked into one model. Start with one agent and scale to a team as your volume grows — with the same account manager guiding the transition.",
  ctaPrompt:
    "Not sure what size fits? Book a free consultation and we'll size the right setup for you.",
};

export const servicesPage = {
  jumpLinks: [
    { label: "Core skills", to: "/services#core-skills" },
    { label: "Who it's for", to: "/services#industries" },
    { label: "Scale a team", to: "/services#scale" },
    { label: "Specialist roles", to: "/services#popular-jobs" },
  ],
  hero: {
    badge: "Services",
    title: "One Main Skill. Three More They've Got Covered.",
    description:
      "Every VA House agent is matched by their strongest discipline — admin, customer support, or calls — with working proficiency in the other two. Need volume, or a dedicated specialist seat? We staff pods, full teams, and specialist roles too.",
  },
  core: {
    eyebrow: "The core offer",
    title: "Multi-skilled agents across three disciplines",
    description:
      "Tell us which task is eating the most of your week. That becomes your agent's main skill. The other two come with them — so overflow doesn't require a second hire.",
  },
  specialists: {
    eyebrow: "Specialist roles",
    title: "Need a dedicated seat instead?",
    description:
      "Some work needs a specialist as the main skill — bookkeeping, video, development, legal admin. We still match on expertise first, with managed backup behind them.",
  },
};

export const homeCostValue = {
  eyebrow: "Cost & value",
  title: "Grow Your Team Without Growing Your Overhead",
  traditionalLabel: "Traditional hiring can involve",
  traditionalItems: [
    "Recruitment",
    "Office Space",
    "Equipment",
    "HR Administration",
    "Training",
    "Benefits",
  ],
  outsourcingLabel: "With outsourcing",
  outsourcingCopy:
    "Get the talent you need while keeping operations lean and scalable.",
};

export const whyPhilippines = {
  eyebrow: "Why the Philippines",
  title: "Build Your Team in the Philippines",
  description:
    "Access a large pool of skilled, English-speaking professionals experienced in supporting international businesses.",
  highlights: [
    "Strong English communication",
    "Experienced BPO workforce",
    "Competitive operating costs",
    "Flexible time-zone coverage",
    "Strong service culture",
  ],
};

export const homeFaqs = [
  {
    question: "Can I interview candidates first?",
    answer:
      "Yes. After we shortlist pre-vetted professionals, you can meet them on intro calls and choose the candidate you prefer before anyone starts.",
  },
  {
    question: "Can your VAs work during my business hours?",
    answer:
      "Yes. We match Filipino talent with overlap for English-speaking markets — including the US, UK, Ireland, Australia, Canada, and New Zealand — so coverage fits your working hours.",
  },
  {
    question: "Can I start with one VA?",
    answer:
      "Yes. Most clients begin with one dedicated Virtual Assistant and add people as the workload grows.",
  },
  {
    question: "Can you provide an entire team?",
    answer:
      "Yes. We can staff a dedicated offshore team across admin, support, sales, and back-office roles, with managed operations as you scale.",
  },
  {
    question: "How do you screen candidates?",
    answer:
      "Every professional is screened for skills, English communication, and reliability before joining our roster. We then match from that pool based on your role, tools, and schedule.",
  },
  {
    question: "How long does hiring take?",
    answer:
      "Most clients are live with their VA in 5–10 business days after discovery and matching.",
  },
  {
    question: "What happens if someone isn't a good fit?",
    answer:
      "We offer a replacement guarantee. If a VA isn't the right fit, we rematch you quickly at no additional matching fee.",
  },
  {
    question: "How do you handle confidentiality?",
    answer:
      "We use contracts, NDAs, and structured onboarding for tool access so your information and client data stay protected.",
  },
  {
    question: "What tools can your VAs use?",
    answer:
      "We onboard talent to the tools you already use — email, CRM, project management, social platforms, helpdesk, and more — and help with access and SOPs during kickoff.",
  },
];

export const homeFinalCta = {
  eyebrow: "Get started",
  title: "Get an Agent Who Grows Your Business, Not Just Your To-Do List.",
  description:
    "Book a free consultation and we'll match you with an agent whose main skill fits your biggest need — or a full team if your volume calls for it — so you get a partner in the work, not just someone checking off tasks.",
  ctaLabel: "Book Your Free Consultation",
};

export const pillars = [
  {
    title: "Vetted Talent Pool",
    description:
      "Every VA is screened for skills, communication, and reliability before joining our roster — no more sifting through hundreds of profiles.",
    icon: "users",
    image: {
      url: "/images/va-pool.jpg",
      alt: "Professional virtual assistant on a video call",
    },
  },
  {
    title: "Expert Matching",
    description:
      "We pair you with assistants whose experience matches your industry, tools, and workflow — not whoever bids lowest.",
    icon: "match",
    image: {
      url: "/images/misraim.png",
      alt: "Team discussing project requirements",
    },
  },
  {
    title: "Managed Operations",
    description:
      "From onboarding to daily oversight and backup coverage, we handle the ops layer so you focus on growing your business.",
    icon: "ops",
    image: {
      url: "/images/vahouse.jpg",
      alt: "Operations dashboard and analytics",
    },
  },
];

export const differentiators = [
  {
    title: "Philippines-based talent",
    description:
      "Skilled Filipino professionals with strong English and experience supporting international teams.",
  },
  {
    title: "Pre-screened professionals",
    description:
      "Every VA is screened for skills, communication, and reliability before joining our roster.",
  },
  {
    title: "Flexible staffing",
    description:
      "Start with one assistant or build a blended team — month-to-month options on core plans.",
  },
  {
    title: "Dedicated remote workers",
    description:
      "Named, dedicated VAs who learn your workflow — not a rotating freelancer pool.",
  },
  {
    title: "Cost-efficient hiring",
    description:
      "Skip local recruitment, office overhead, and HR admin while still getting full-time support.",
  },
  {
    title: "Client-focused matching",
    description:
      "We pair you with talent whose experience matches your industry, tools, and timezone.",
  },
  {
    title: "Ongoing support",
    description:
      "Account managers, check-ins, and replacement guarantees keep delivery consistent.",
  },
  {
    title: "Scalable teams",
    description:
      "Add people as you grow — from a single VA to a dedicated offshore team.",
  },
];

/** English-speaking markets we serve — clients outsourcing to the Philippines */
export const clientCoverage = {
  headline: "English-speaking countries outsourcing to the Philippines",
  description:
    "We partner with businesses in the US, UK, Ireland, Australia, Canada, and New Zealand — English-speaking teams who want reliable Filipino talent with managed operations, not DIY overseas hiring.",
  philippinesNote:
    "Our VAs are based in the Philippines with professional English and proven experience supporting international clients.",
  philippinesFlagCode: "ph",
  countries: [
    { id: "us", label: "United States", shortLabel: "US", flagCode: "us" },
    { id: "uk", label: "United Kingdom", shortLabel: "UK", flagCode: "gb" },
    { id: "ie", label: "Ireland", shortLabel: "IE", flagCode: "ie" },
    { id: "au", label: "Australia", shortLabel: "AU", flagCode: "au" },
    { id: "ca", label: "Canada", shortLabel: "CA", flagCode: "ca" },
    { id: "nz", label: "New Zealand", shortLabel: "NZ", flagCode: "nz" },
  ],
};

/** Flag image URL (flagcdn.com) — width in px; use flagCode from clientCoverage.countries */
export function countryFlagUrl(flagCode, width = 80) {
  if (!flagCode) return "";
  return `https://flagcdn.com/w${width}/${flagCode.toLowerCase()}.png`;
}

export const trustBadges = [
  "Free Premium VA trial",
  "Philippines-based Filipino VAs",
  "English-speaking client markets",
  "Replacement guarantee",
  "Dedicated account manager",
];

/**
 * Trust signals with icons — shown in the trusted-by section.
 * Add logoUrl on companies when you have client logos in public/images/clients/
 */
export const trustSignals = [
  {
    id: "vetted",
    label: "Vetted talent",
    description: "Multi-step screening before placement",
  },
  {
    id: "managed",
    label: "Fully managed",
    description: "Account managers & performance QA",
  },
  {
    id: "backup",
    label: "Backup coverage",
    description: "Continuity on Pro+ plans",
  },
  {
    id: "markets",
    label: "Global coverage",
    description: "US · UK · AU · CA · NZ · IE",
  },
];

/** Placeholder client logos — swap for real names/logos in public/images/clients/ when ready */
export const trustedBy = {
  eyebrow: "Social proof",
  title: "Trusted by businesses across multiple industries",
  description:
    "From ecommerce operators to agencies and real estate teams — English-speaking businesses trust our managed VA model.",
  companies: [
    {
      name: "Northline Commerce",
      industry: "Ecommerce · United States",
      initials: "NC",
      logoUrl: "",
    },
    {
      name: "Harbour & Co. Realty",
      industry: "Real estate · Australia",
      initials: "HR",
      logoUrl: "",
    },
    {
      name: "Brightfield Agency",
      industry: "Marketing agency · United Kingdom",
      initials: "BA",
      logoUrl: "",
    },
    {
      name: "Summit SaaS Labs",
      industry: "Software · Remote-first",
      initials: "SS",
      logoUrl: "",
    },
    {
      name: "Elevate Coaching Group",
      industry: "Coaching & creators · Global",
      initials: "EC",
      logoUrl: "",
    },
    {
      name: "Keystone Advisory",
      industry: "Professional services · US & UK",
      initials: "KA",
      logoUrl: "",
    },
    {
      name: "Wellspring Clinics",
      industry: "Healthcare · United States",
      initials: "WC",
      logoUrl: "",
    },
    {
      name: "Outbound Partners",
      industry: "B2B sales · English-speaking markets",
      initials: "OP",
      logoUrl: "",
    },
  ],
};

export const heroStats = [
  { value: "20+", label: "Professionals", icon: "team" },
  { value: "6", label: "Countries Supported", icon: "globe" },
  { value: "5–10 days", label: "Typical hiring time", icon: "clock" },
  { value: "98%", label: "Weekly task completion", icon: "check" },
];

/** Gallery strip on homepage — add or replace URLs (or use /images/your-file.jpg from public/) */
export const heroImages = [
  {
    url: "/images/va-office1.jpg",
    alt: "Virtual Assistant House reception and branded workspace",
  },
  {
    url: "/images/vaoffice.jpg",
    alt: "Modern BPO lounge and collaboration area",
  },
  {
    url: "/images/VAHOUSE-134-scaled.jpg",
    alt: "Professional meeting room in the Philippines office",
  },
];

/** Site images — edit URLs here to change photos across the site */
export const images = {
  hero: {
    primary: {
      url: "/images/VAHOUSE-127.jpg",
      alt: "Professional BPO workspace at Virtual Assistant House in the Philippines",
    },
    gallery: heroImages,
  },
  about: {
    url: "/images/pic2.jpg",
    alt: "Remote team collaboration",
  },
  contact: {
    url: "/images/vabuilding.jpg",
    alt: "Business consultation meeting",
  },
  cta: {
    url: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=1200&q=80",
    alt: "Team planning session",
  },
  /** Background photos for inner page heroes — pass to PageHero via :background-image */
  pageHero: {
    pricing: {
      url: "/images/vaoffice.jpg",
      overlay: 0.62,
    },
    services: {
      url: "/images/VAHOUSE-127.jpg",
      overlay: 0.6,
    },
    about: {
      url: "/images/vaoffice2.jpg",
      overlay: 0.58,
    },
    contact: {
      url: "/images/VAHOUSE-126-scaled.jpg",
      overlay: 0.6,
    },
    howItWorks: {
      url: "/images/va-pool.jpg",
      overlay: 0.58,
    },
    caseStudies: {
      url: "/images/pic6.jpg",
      overlay: 0.62,
    },
    testimonials: {
      url: "/images/VAHOUSE-134-scaled.jpg",
      overlay: 0.6,
    },
    faq: {
      url: "/images/faq.jpg",
      overlay: 0.6,
    },
    blog: {
      url: "/images/VAHOUSE-123-scaled.jpg",
      overlay: 0.58,
    },
    /** Default for SEO landing pages (role, market, niche, comparison) */
    seo: {
      url: "/images/VAHOUSE-135-scaled.jpg",
      overlay: 0.65,
    },
  },
  /** Homepage services preview — swap for /images/services/your-file.jpg when ready */
  homeServices: {
    categories: {
      "Operations & admin": {
        url: "/images/Image.jpg",
        alt: "Virtual assistant managing admin tasks",
      },
      "Sales & call center": {
        url: "/images/april.jpg",
        alt: "Customer support and sales calls",
      },
      "Media & creative": {
        url: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&w=800&q=80",
        alt: "Video editing and creative production",
      },
      "Technology & data": {
        url: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=800&q=80",
        alt: "Developer and technology workspace",
      },
      "Finance & HR": {
        url: "/images/meeting.jpg",
        alt: "Accounting and finance workspace",
      },
      "Industry specialists": {
        url: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
        alt: "Real estate professional workspace",
      },
    },
    roles: {
      "ai-video-editor": {
        url: "https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?auto=format&fit=crop&w=800&q=80",
        alt: "AI-assisted video editing workflow",
      },
      "video-editor": {
        url: "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&w=800&q=80",
        alt: "Video editor at work",
      },
      "social-media-manager": {
        url: "https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?q=80&w=2070&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
        alt: "Social media content planning",
      },
      "ai-va": {
        url: "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80",
        alt: "AI tools and virtual assistance",
      },
      "automation-specialist": {
        url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
        alt: "Automation dashboards and workflows",
      },
      "call-center": {
        url: "https://images.pexels.com/photos/8101312/pexels-photo-8101312.jpeg",
        alt: "Call center team with headsets",
      },
      "ecommerce-developer": {
        url: "https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=800&q=80",
        alt: "Ecommerce store management",
      },
      sales: {
        url: "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&w=800&q=80",
        alt: "Sales team collaboration",
      },
      "general-va": {
        url: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=800&q=80",
        alt: "General virtual assistant",
      },
      "executive-assistant": {
        url: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=800&q=80",
        alt: "Executive assistant support",
      },
      callers: {
        url: "https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=800&q=80",
        alt: "Outbound calling and lead follow-up",
      },
      bookkeeper: {
        url: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=800&q=80",
        alt: "Bookkeeping and accounting",
      },
      "real-estate": {
        url: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
        alt: "Real estate virtual assistant",
      },
    },
    industries: {
      "real-estate": {
        url: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
        alt: "Real estate team",
      },
      ecommerce: {
        url: "https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=800&q=80",
        alt: "Ecommerce operations",
      },
      saas: {
        url: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80",
        alt: "SaaS and technology startup",
      },
      "professional-services": {
        url: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80",
        alt: "Professional services agency",
      },
      "healthcare-wellness": {
        url: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80",
        alt: "Healthcare and wellness clinic",
      },
      "coaching-creators": {
        url: "https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&w=800&q=80",
        alt: "Creator and coaching brand",
      },
      "media-content": {
        url: "https://images.unsplash.com/photo-1598899134739-652d0d19e309?auto=format&fit=crop&w=800&q=80",
        alt: "Media and content production",
      },
      "b2b-sales": {
        url: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80",
        alt: "B2B sales team",
      },
    },
  },
};

/** Default role image for homepage cards when a role id is not in images.homeServices.roles */
export function getHomeServiceRoleImage(roleId) {
  return (
    images.homeServices.roles[roleId] ?? {
      url: images.hero.primary.url,
      alt: "Managed virtual assistant role",
    }
  );
}

/** Industry image for homepage industry tiles */
export function getHomeServiceIndustryImage(industryId) {
  return (
    images.homeServices.industries[industryId] ?? {
      url: images.pageHero.services.url,
      alt: "Industry we support",
    }
  );
}

/** Homepage pricing section copy — tiers still come from pricingTiers */
export const homePricing = {
  eyebrow: "Pricing",
  title: "Flexible plans for every growth stage",
  description:
    "Managed Filipino VAs with matching, oversight, and backup coverage — from one assistant to full teams. Professional and Growth include a free Premium VA trial.",
  includesLabel: "Every plan includes",
  includes: [
    "Vetted Filipino talent",
    "Expert role matching",
    "Account manager & QA",
    "Replacement guarantee",
  ],
  footnote:
    "From $9/hr · USD · Month-to-month · No hidden setup fees · Custom quotes for Enterprise",
  compareCta: "Compare plans & FAQ",
};

/** Client-facing VA labor base rate — tier monthly prices = hourly rate × bundled hours */
export const pricingBase = {
  hourlyRateUsd: 9,
  weeksPerMonth: 4,
  hoursPerWeekFullTime: 40,
  hoursPerWeekPartTime: 20,
};

const FULL_TIME_MONTHLY_HOURS =
  pricingBase.hoursPerWeekFullTime * pricingBase.weeksPerMonth;
const PART_TIME_MONTHLY_HOURS =
  pricingBase.hoursPerWeekPartTime * pricingBase.weeksPerMonth;

function monthlyVaPrice(hours) {
  return pricingBase.hourlyRateUsd * hours;
}

function formatTierPrice(amount) {
  return `Starts from $${amount.toLocaleString("en-US")}/mo`;
}

export const pricingTiers = [
  {
    id: "starter",
    name: "Starter",
    audience: "Solopreneurs & founders",
    price: formatTierPrice(monthlyVaPrice(PART_TIME_MONTHLY_HOURS)),
    hourlyRate: pricingBase.hourlyRateUsd,
    monthlyHours: PART_TIME_MONTHLY_HOURS,
    description: "One dedicated VA for essential admin and operational tasks.",
    features: [
      "1 dedicated virtual assistant",
      "80 VA hours/month (20 hrs/week)",
      "Skills matched to your needs",
      "Weekly performance check-ins",
      "Email & chat support",
      "30-day satisfaction guarantee",
    ],
    recommended: false,
    cta: "Get started",
  },
  {
    id: "professional",
    name: "Professional",
    audience: "Growing businesses",
    price: formatTierPrice(monthlyVaPrice(FULL_TIME_MONTHLY_HOURS)),
    hourlyRate: pricingBase.hourlyRateUsd,
    monthlyHours: FULL_TIME_MONTHLY_HOURS,
    description:
      "Premium Virtual Assistance with backup coverage for uninterrupted support.",
    features: [
      "Free trial available",
      "1 dedicated VA + backup coverage",
      "160 VA hours/month (40 hrs/week)",
      "Priority matching within 5 days",
      "Bi-weekly strategy calls",
      "Tool onboarding assistance",
      "Performance reporting dashboard",
    ],
    recommended: false,
    freeTrialEligible: true,
    cta: "Start free trial",
  },
  {
    id: "growth",
    name: "Growth",
    audience: "Teams scaling fast",
    price: formatTierPrice(monthlyVaPrice(FULL_TIME_MONTHLY_HOURS * 2)),
    hourlyRate: pricingBase.hourlyRateUsd,
    monthlyHours: FULL_TIME_MONTHLY_HOURS * 2,
    description:
      "Premium multi-VA support with a project manager — our most popular plan.",
    features: [
      "Free trial available",
      "2–3 dedicated virtual assistants",
      "320+ VA hours/month (from 2 full-time VAs)",
      "Dedicated project manager",
      "Cross-VA coordination & QA",
      "Custom SOP development",
      "Monthly business review",
      "Priority support & replacements",
    ],
    recommended: true,
    freeTrialEligible: true,
    cta: "Start free trial",
  },
  {
    id: "enterprise",
    name: "Enterprise",
    audience: "SMEs & agencies",
    price: "Custom pricing",
    description: "Full VA teams with custom SLAs for larger operations.",
    features: [
      "Custom team size & roles",
      "Dedicated client success manager",
      "Custom SLAs & reporting",
      "Multi-department coverage",
      "Quarterly strategic planning",
      "Volume pricing available",
    ],
    recommended: false,
    cta: "Contact sales",
  },
];

/** End-to-end process for hiring a VA through VA House */
export const hiringProcess = {
  eyebrow: "How it works",
  title: "Building Your Remote Team Is Simple",
  description:
    "From your first conversation to a fully managed VA on your team — we handle vetting, matching, onboarding, and day-to-day operations.",
  timelineNote: "Most clients are live with their VA in 5–10 business days",
  steps: [
    {
      step: 1,
      title: "Tell Us What You Need",
      timing: "Day 1",
      summary: "We learn about your role, tasks, schedule, and requirements.",
      description:
        "We learn about your role, tasks, schedule, and requirements — then map the right support plan.",
      client: [
        "Book a discovery call or submit the contact form",
        "Share goals, task list, tools, and preferred working hours",
      ],
      vaHouse: [
        "Recommend the right support tier",
        "Define role scope, outcomes, and onboarding timeline",
      ],
    },
    {
      step: 2,
      title: "We Find Candidates",
      timing: "Days 2–4",
      summary: "We screen professionals based on your requirements.",
      description:
        "We screen professionals from our vetted roster based on your requirements — not open-market applicants you have to filter yourself.",
      client: [
        "Review shortlisted profiles",
        "Join optional intro calls with top candidates",
      ],
      vaHouse: [
        "Match from our vetted talent pool",
        "Coordinate intros and incorporate your feedback",
      ],
    },
    {
      step: 3,
      title: "Interview Your Candidates",
      timing: "Days 4–7",
      summary: "Meet shortlisted applicants and choose your preferred candidate.",
      description:
        "Meet shortlisted applicants, choose your preferred candidate, then we handle contracts, confidentiality, and tool access.",
      client: [
        "Confirm your hire and approve the start date",
        "Grant tool access and share SOPs or training materials",
      ],
      vaHouse: [
        "Contracts, NDAs, and compliance-ready paperwork",
        "Structured kickoff plan and tool onboarding support",
      ],
    },
    {
      step: 4,
      title: "Start Working Together",
      timing: "Days 7–10",
      summary: "We assist with onboarding and getting your VA started.",
      description:
        "We assist with onboarding and getting your VA started — real work, clear priorities, and account-manager oversight in week one.",
      client: [
        "Assign first tasks and set check-in cadence",
        "Give feedback on tone, quality, and priorities",
      ],
      vaHouse: [
        "Account manager oversight during week one",
        "QA on early deliverables and workflow adjustments",
      ],
    },
    {
      step: 5,
      title: "Scale When You're Ready",
      timing: "Ongoing",
      summary: "Add more people as your business grows.",
      description:
        "Add more people as your business grows. We keep oversight, backup coverage, and replacements in place as you expand.",
      client: [
        "Focus on outcomes and scale responsibilities over time",
        "Escalate issues to your account manager when needed",
      ],
      vaHouse: [
        "Weekly check-ins and performance reporting",
        "Backup coverage, coaching, and replacement guarantee",
      ],
    },
  ],
};

/** @deprecated Use hiringProcess.steps — kept for existing imports */
export const howItWorksSteps = hiringProcess.steps;

/** Checklist shown on How It Works — helps clients prepare for step 1 */
export const hiringPrep = [
  {
    title: "Task list & priorities",
    description:
      "List recurring tasks, tools involved, and what “done well” looks like — even a rough bullet list is enough to start.",
  },
  {
    title: "Tools & access",
    description:
      "Note which apps your VA will use (email, CRM, project tools, social platforms) so we plan secure access during onboarding.",
  },
  {
    title: "Timezone & hours",
    description:
      "Tell us your country and preferred business hours (US, UK, AU, CA, NZ, IE) so we match a VA in the Philippines with the right overlap.",
  },
  {
    title: "Success metrics",
    description:
      "Hours saved per week, response times, leads handled — clear outcomes help us scope the role and track performance.",
  },
];

export const pricingFaqs = [
  {
    question: "Do you offer a free trial for Premium Virtual Assistance?",
    answer:
      "Yes. New clients can start with a free trial of our Premium Virtual Assistance — fully managed VA support with vetted talent, matching, and operational oversight. Contact us to confirm eligibility and trial length for your plan.",
  },
  {
    question: "How is this different from hiring on Upwork?",
    answer:
      "Upwork puts the hiring, vetting, and management burden on you. VA House provides pre-vetted talent, handles matching, and manages day-to-day operations — so you get reliability, not roulette.",
  },
  {
    question: "Can I hire a VA directly for less?",
    answer:
      "Possibly — but you take on recruiting, training, QA, backup coverage, and churn risk. Our managed model saves you time and protects continuity, which is why clients stay on Growth plans longest.",
  },
  {
    question: "What if my VA doesn't work out?",
    answer:
      "We offer a replacement guarantee. If a VA isn't the right fit, we rematch you quickly at no additional matching fee.",
  },
  {
    question: "Are there long-term contracts?",
    answer:
      "Starter and Professional plans start month-to-month. Growth and Enterprise plans offer discounted rates with quarterly commitments.",
  },
];

export const aboutHighlights = [
  {
    title: "Human-first matching",
    description:
      "We match for communication style, work habits, and tools, not just keyword checklists.",
  },
  {
    title: "Operational accountability",
    description:
      "Account managers oversee onboarding, KPIs, and consistency so your team gets reliable output.",
  },
  {
    title: "Continuity built in",
    description:
      "Backup coverage and replacement guarantees protect your operations as your business scales.",
  },
];

/** Industries where our VAs have proven client experience */
export const vaIndustryExperience = [
  {
    id: "real-estate",
    name: "Real estate",
    description:
      "Agents, brokerages, and property teams — listing coordination, lead follow-up, CRM hygiene, and client communication.",
    roles: [
      "Real Estate VA",
      "Callers",
      "Call Center",
      "Executive Assistant",
      "Social Media Manager",
      "Project Manager",
      "Sales",
    ],
  },
  {
    id: "ecommerce",
    name: "Ecommerce & DTC",
    description:
      "Shopify, Amazon, and multichannel brands — operations, support, listings, reporting, and store growth workflows.",
    roles: [
      "Ecommerce Developer",
      "General VA",
      "Call Center",
      "Callers",
      "Data Analyst",
      "Social Media Manager",
      "AI Virtual Assistant",
      "Bookkeeper",
    ],
  },
  {
    id: "saas",
    name: "SaaS & technology",
    description:
      "B2B software and tech startups — sales support, onboarding, documentation, IT help desk, and automation.",
    roles: [
      "AI Virtual Assistant",
      "Sales",
      "IT Support",
      "Web Developer",
      "Automation Specialist",
      "Project Manager",
    ],
  },
  {
    id: "professional-services",
    name: "Professional services",
    description:
      "Consultancies, agencies, and service firms — executive support, scheduling, proposals, and client delivery ops.",
    roles: [
      "Executive Assistant",
      "General VA",
      "Project Manager",
      "Bookkeeper",
    ],
  },
  {
    id: "healthcare-wellness",
    name: "Healthcare & wellness",
    description:
      "Clinics and wellness brands — appointment setting, patient intake support, and front-desk style coordination.",
    roles: ["Callers", "Call Center", "General VA", "Executive Assistant"],
  },
  {
    id: "coaching-creators",
    name: "Coaching & creators",
    description:
      "Coaches, creators, and personal brands — video editing, social content, community, and launch support.",
    roles: [
      "Video Editor",
      "AI Video Editor",
      "Social Media Manager",
      "General VA",
      "Executive Assistant",
    ],
  },
  {
    id: "media-content",
    name: "Media & content teams",
    description:
      "Agencies, brands, and creators scaling video — short-form, YouTube, ads, and AI-assisted editing pipelines.",
    roles: [
      "Video Editor",
      "AI Video Editor",
      "Social Media Manager",
      "AI Virtual Assistant",
    ],
  },
  {
    id: "b2b-sales",
    name: "B2B sales teams",
    description:
      "Outbound and inbound pipeline support — prospecting, CRM updates, meeting booking, and follow-up sequences.",
    roles: ["Sales", "Callers", "Call Center", "Data Analyst"],
  },
  {
    id: "hr-recruiting",
    name: "Growing employers",
    description:
      "Teams scaling headcount — sourcing support, screening coordination, onboarding docs, and HR admin.",
    roles: ["HR / Recruiter VA", "General VA", "Project Manager"],
  },
];

/** Managed VA roles available through VA House */
export const serviceRoles = [
  {
    id: "general-va",
    title: "General Virtual Assistant",
    category: "Operations & admin",
    description:
      "Day-to-day admin, inbox, scheduling, research, CRM updates, and recurring business tasks.",
    industries: [
      "Ecommerce",
      "Real estate",
      "Professional services",
      "Coaching & creators",
    ],
    highlights: [
      "Email & calendar management",
      "CRM & data entry",
      "Research & reporting",
      "Document prep",
    ],
  },
  {
    id: "executive-assistant",
    title: "Executive Assistant",
    category: "Operations & admin",
    description:
      "High-trust support for founders and executives — calendar control, inbox triage, travel, and meeting prep.",
    industries: [
      "Professional services",
      "SaaS & technology",
      "Real estate",
      "Coaching & creators",
    ],
    highlights: [
      "Executive inbox & calendar",
      "Travel & meeting coordination",
      "Stakeholder communication",
      "Confidential document handling",
    ],
  },
  {
    id: "project-manager",
    title: "Project Manager",
    category: "Operations & admin",
    description:
      "Task coordination, timelines, SOPs, and cross-team delivery so projects stay on track.",
    industries: ["Ecommerce", "Agencies", "Real estate", "SaaS & technology"],
    highlights: [
      "Sprint & task tracking",
      "Team coordination",
      "SOP documentation",
      "Status reporting",
    ],
  },
  {
    id: "callers",
    title: "Callers",
    category: "Sales & call center",
    description:
      "Outbound and inbound calling for lead follow-up, qualification, and appointment setting.",
    industries: [
      "Real estate",
      "B2B sales",
      "Healthcare & wellness",
      "Professional services",
    ],
    highlights: [
      "Lead follow-up calls",
      "Appointment setting",
      "Qualification scripts",
      "English-speaking market hours",
    ],
  },
  {
    id: "sales",
    title: "Sales",
    category: "Sales & call center",
    description:
      "Pipeline support — prospecting, CRM hygiene, sequences, and meeting prep for closers.",
    industries: [
      "SaaS & technology",
      "B2B sales",
      "Ecommerce",
      "Professional services",
    ],
    highlights: [
      "CRM updates & hygiene",
      "Outbound sequences",
      "Lead research",
      "Proposal & deck support",
    ],
    trending: true,
  },
  {
    id: "call-center",
    title: "Call Center Support",
    category: "Sales & call center",
    description:
      "Scalable calling teams for inbound support, outbound campaigns, and high-volume follow-up — fully managed.",
    industries: [
      "Real estate",
      "Healthcare & wellness",
      "Ecommerce",
      "B2B services",
    ],
    highlights: [
      "Inbound & outbound teams",
      "Scripted campaigns",
      "QA & call monitoring",
      "Backup coverage",
    ],
    featured: true,
  },
  {
    id: "video-editor",
    title: "Video Editor",
    category: "Media & creative",
    description:
      "Professional video editing for YouTube, ads, courses, and social — cuts, pacing, captions, color, and platform-ready exports.",
    industries: [
      "Coaching & creators",
      "Ecommerce",
      "Agencies",
      "Real estate",
      "Media & content",
    ],
    highlights: [
      "Premiere Pro / DaVinci Resolve / CapCut",
      "Short-form, long-form & ad edits",
      "Captions, thumbnails & basic motion",
      "Brand-aligned delivery",
    ],
    trending: true,
  },
  {
    id: "ai-video-editor",
    title: "AI Video Editor",
    category: "Media & creative",
    description:
      "AI-powered editing workflows — fast social clips, auto-captions, B-roll, repurposing, and tool stacks like Descript, Runway, and CapCut with human QA.",
    industries: [
      "Coaching & creators",
      "Ecommerce",
      "Agencies",
      "Media & content",
      "SaaS & technology",
    ],
    highlights: [
      "AI tools (Descript, Runway, Opus Clip, etc.)",
      "Long-form to shorts repurposing",
      "Auto captions & subtitles",
      "High-volume social output",
    ],
    trending: true,
  },
  {
    id: "social-media-manager",
    title: "Social Media Manager",
    category: "Media & creative",
    description:
      "Content scheduling, community management, analytics, and platform-specific posting.",
    industries: [
      "Ecommerce",
      "Coaching & creators",
      "Real estate",
      "Agencies",
      "Media & content",
    ],
    highlights: [
      "Content calendars",
      "Community engagement",
      "Canva & basic creative",
      "Performance reporting",
    ],
    trending: true,
  },
  {
    id: "ai-va",
    title: "AI Virtual Assistant",
    category: "Technology & data",
    description:
      "AI-enabled support using ChatGPT, Claude, and your stack — research, drafts, summaries, prompt libraries, and AI-assisted workflows with human QA.",
    industries: [
      "SaaS & technology",
      "Ecommerce",
      "Agencies",
      "Professional services",
      "Coaching & creators",
    ],
    highlights: [
      "ChatGPT & Claude daily ops",
      "AI research & content drafts",
      "Prompt templates & SOPs",
      "Tool integrations (Zapier, Notion, etc.)",
    ],
    trending: true,
  },
  {
    id: "automation-specialist",
    title: "Automation Specialist",
    category: "Technology & data",
    description:
      "Workflow automation across Zapier, Make, CRMs, and AI tools to reduce manual work.",
    industries: [
      "SaaS & technology",
      "Ecommerce",
      "Agencies",
      "Professional services",
    ],
    highlights: [
      "Zapier / Make workflows",
      "AI + no-code automations",
      "CRM automations",
      "Process documentation",
    ],
    trending: true,
  },
  {
    id: "data-analyst",
    title: "Data Analyst",
    category: "Technology & data",
    description:
      "Reporting, dashboards, and data cleanup so leaders can make decisions from accurate numbers.",
    industries: [
      "Ecommerce",
      "SaaS & technology",
      "Marketing agencies",
      "B2B sales",
    ],
    highlights: [
      "Spreadsheets & dashboards",
      "KPI reporting",
      "Data cleaning",
      "Ad & sales analytics",
    ],
    trending: true,
  },
  {
    id: "web-developer",
    title: "Web Developer",
    category: "Technology & data",
    description:
      "WordPress, landing pages, site updates, and front-end maintenance with managed delivery oversight.",
    industries: [
      "Agencies",
      "Ecommerce",
      "SaaS & technology",
      "Professional services",
    ],
    highlights: [
      "WordPress & Webflow",
      "Landing page builds",
      "Site maintenance",
      "Basic SEO implementation",
    ],
  },
  {
    id: "ecommerce-developer",
    title: "Ecommerce Developer",
    category: "Technology & data",
    description:
      "Shopify and marketplace store setup, theme edits, product uploads, and conversion-focused improvements.",
    industries: ["Ecommerce & DTC", "Retail brands", "Amazon sellers"],
    highlights: [
      "Shopify & WooCommerce",
      "Product listings",
      "Theme & CRO tweaks",
      "App integrations",
    ],
    trending: true,
  },
  {
    id: "it-support",
    title: "IT Support",
    category: "Technology & data",
    description:
      "Help desk style support for tools, accounts, troubleshooting, and internal tech coordination.",
    industries: [
      "SaaS & technology",
      "Remote teams",
      "Ecommerce",
      "Professional services",
    ],
    highlights: [
      "Tool onboarding",
      "Ticket triage",
      "Account & access admin",
      "Vendor coordination",
    ],
  },
  {
    id: "real-estate",
    title: "Real Estate VA",
    category: "Industry specialists",
    description:
      "Listing coordination, MLS updates, lead nurture, transaction admin, and agent support workflows.",
    industries: [
      "Residential & commercial real estate",
      "Property management",
      "Brokerages",
    ],
    highlights: [
      "Listing & MLS support",
      "Lead follow-up",
      "Transaction coordination",
      "CRM (Follow Up Boss, KVCore, etc.)",
    ],
  },
  {
    id: "bookkeeper",
    title: "Accounting & Bookkeeper",
    category: "Finance & HR",
    description:
      "Bookkeeping support, reconciliations, invoicing, AP/AR, and prep for your accountant or CPA.",
    industries: [
      "Ecommerce",
      "Real estate",
      "Professional services",
      "Growing SMEs",
    ],
    highlights: [
      "QuickBooks & Xero",
      "Reconciliations",
      "Invoicing & AP/AR",
      "Monthly reporting prep",
    ],
  },
  {
    id: "hr-recruiter",
    title: "HR / Recruiter",
    category: "Finance & HR",
    description:
      "Sourcing support, screening coordination, onboarding paperwork, and HR admin for growing teams.",
    industries: [
      "Growing employers",
      "Agencies",
      "Ecommerce",
      "SaaS & technology",
    ],
    highlights: [
      "Candidate sourcing",
      "Interview scheduling",
      "Onboarding docs",
      "HRIS admin support",
    ],
  },
];

/** High-demand roles highlighted on the services page (includes AI) */
export const popularJobsNow = {
  sectionId: "popular-jobs",
  eyebrow: "In demand",
  title: "Specialist seats clients hire when one lane isn't enough",
  description:
    "When the work is specialized — video, social, automation, ecommerce builds — we match a VA whose main skill is that specialty, with the same managed backup behind them.",
  roleIds: [
    "ai-video-editor",
    "video-editor",
    "social-media-manager",
    "ai-va",
    "automation-specialist",
    "call-center",
    "ecommerce-developer",
    "sales",
  ],
};

/** Group service roles by category for the services page */
export const serviceCategories = [
  "Operations & admin",
  "Sales & call center",
  "Media & creative",
  "Technology & data",
  "Finance & HR",
  "Industry specialists",
].map((name) => ({
  name,
  roles: serviceRoles.filter((role) => role.category === name),
}));

/** @deprecated Use serviceCategories — kept for any legacy imports */
export const servicesOverview = serviceCategories.map((category) => ({
  title: category.name,
  description: category.roles[0]?.description ?? "",
  roles: category.roles.map((r) => r.title),
  image: null,
}));

/** Client testimonials — edit quotes in one place; optional imageUrl per person */
export const testimonials = {
  eyebrow: "Social proof",
  title: "What Clients Say",
  description:
    "Founders and operators across English-speaking markets share their experience with our managed Filipino VA teams.",
  items: [
    {
      id: "shay-better",
      quote:
        "We have been thoroughly impressed with her strong work ethic and believe she is exceptionally well-suited for the EA role. Thank you for keeping us updated and making the transition seamless.",
      name: "Shay Better",
      role: "Founder",
      company: "Shay Better Coaching",
      location: "United States",
      initials: "SB",
      imageUrl: "",
      rating: 5,
    },
    {
      id: "phil-betito",
      quote:
        "I am blessed and grateful to meet you and Pauline. I am so thrilled to embark on this startup — you are the perfect fit, a very talented technopreneur, just what I have been searching for.",
      name: "Phil Betito",
      role: "Founder",
      company: "NY Golf Shuttle",
      location: "United States",
      initials: "PB",
      imageUrl: "",
      rating: 5,
    },
    {
      id: "marcus-simmonds",
      quote:
        "Mike has been a fantastic support in our collaboration. His contributions have certainly been appreciated — looking forward to continuing our fruitful partnership.",
      name: "Marcus Simmonds",
      role: "Client",
      company: "International business",
      location: "Cayman Islands",
      initials: "MS",
      imageUrl: "",
      rating: 5,
    },
    {
      id: "karen-grabowski",
      quote:
        "Thank you for your amazing partnership from the very beginning on this last-minute request. Charles and Marenela were willing to meet on short notice — your efforts do not go unnoticed.",
      name: "Karen Grabowski",
      role: "Client",
      company: "Business operations",
      location: "United States",
      initials: "KG",
      imageUrl: "",
      rating: 5,
    },
    {
      id: "stu-jordan",
      quote:
        "I enjoyed working with Joseph and highly recommend VA House for anyone who wants reliable, managed virtual assistant support.",
      name: "Stu Jordan",
      role: "Co-Founder",
      company: "Red Jay Digital",
      location: "New Zealand",
      initials: "SJ",
      imageUrl: "",
      rating: 5,
    },
    {
      id: "kate-waterhouse",
      quote:
        "I started with one VA and ended up with a virtual team in the Philippines. I can take on more projects because I delegate with confidence — and I now recommend VA House to my own clients.",
      name: "Kate Waterhouse",
      role: "Director",
      company: "Agency & consulting",
      location: "Australia",
      initials: "KW",
      imageUrl: "",
      rating: 5,
    },
  ],
};

export const caseStudies = [
  {
    company: "US Ecommerce Brand",
    challenge:
      "Founder was spending 15+ hours weekly on support and order operations.",
    solution:
      "Matched with a dedicated operations VA and created SOPs in first 2 weeks.",
    results: [
      "42% faster support response times",
      "10+ hours/week returned to founder",
      "98% weekly task completion",
    ],
    image: {
      url: "https://images.unsplash.com/photo-1472851294608-062f824d29cc?auto=format&fit=crop&w=800&q=80",
      alt: "Ecommerce business operations",
    },
  },
  {
    company: "Australian Real Estate Team",
    challenge:
      "Lead follow-up and listing coordination were inconsistent across agents.",
    solution:
      "Assigned two VAs with a project manager for lead routing and checklist QA.",
    results: [
      "31% increase in follow-up consistency",
      "25% faster listing turnaround",
      "Reduced admin bottlenecks across team",
    ],
    image: {
      url: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
      alt: "Real estate professional workspace",
    },
  },
  {
    company: "UK Agency",
    challenge:
      "Client delivery tasks were fragmented and hard to delegate reliably.",
    solution:
      "Created role-based handoffs between VA pods with weekly performance reviews.",
    results: [
      "20% improvement in on-time deliverables",
      "Lower client churn risk",
      "Scalable process for new client onboarding",
    ],
    image: {
      url: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80",
      alt: "Agency team workspace",
    },
  },
];

export const faqCategories = [
  {
    title: "Getting started",
    items: [
      {
        question: "How quickly can we start?",
        answer:
          "Most clients begin within 5-10 business days after discovery and matching.",
      },
      {
        question: "Do you help define the VA role?",
        answer:
          "Yes. We help map responsibilities, outcomes, and tools before onboarding starts.",
      },
      {
        question: "Is there a free trial for Premium Virtual Assistance?",
        answer:
          "Yes. We offer a free trial so you can experience our Premium Virtual Assistance before committing. Reach out via the contact form to get started and confirm details for your plan.",
      },
    ],
  },
  {
    title: "Management & quality",
    items: [
      {
        question: "Who manages day-to-day performance?",
        answer:
          "Your account manager provides oversight, check-ins, and performance support with your VA.",
      },
      {
        question: "What if performance drops?",
        answer:
          "We intervene quickly with coaching, process adjustments, and replacement options when needed.",
      },
    ],
  },
  {
    title: "Pricing & contracts",
    items: [
      {
        question: "Are there setup fees?",
        answer:
          "No hidden setup fees. Pricing is clear by tier and scope from the start.",
      },
      {
        question: "Can we scale up later?",
        answer:
          "Yes. Most clients start with one VA and expand to multi-VA support as they grow.",
      },
    ],
  },
];

export const footerColumns = [
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      {
        label: "Careers",
        href: `mailto:${contact.email}?subject=Careers at Virtual Assistant House`,
      },
      { label: "Contact", to: "/contact" },
      { label: "Pricing", to: "/pricing" },
    ],
  },
  {
    title: "Services",
    links: [
      { label: "Virtual Assistants", to: "/services#general-va" },
      { label: "Customer Support", to: "/services#call-center" },
      { label: "Sales Support", to: "/services#sales" },
      { label: "Back Office", to: "/services#data-analyst" },
      { label: "Dedicated Teams", to: "/services" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "FAQ", to: "/faq" },
      { label: "Blog", to: "/blog" },
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms", to: "/terms-of-service" },
    ],
  },
  {
    title: "Markets",
    links: [
      { label: "US Clients", to: "/virtual-assistants-for-us-clients" },
      { label: "UK Clients", to: "/va-for-uk" },
      { label: "Australia", to: "/va-for-australia" },
      { label: "Canada", to: "/va-for-canada" },
      { label: "New Zealand", to: "/va-for-new-zealand" },
    ],
  },
];

export const seoInternalLinks = [
  { label: "All VA services", to: "/services" },
  { label: "AI Virtual Assistant", to: "/va/ai-va" },
  { label: "Video Editor", to: "/va/video-editor" },
  { label: "AI Video Editor", to: "/va/ai-video-editor" },
  { label: "Popular roles", to: "/services#popular-jobs" },
  { label: "Call center teams", to: "/services#call-center" },
  { label: "Real estate VA", to: "/services#real-estate" },
  { label: "Ecommerce developer", to: "/services#ecommerce-developer" },
  { label: "VAs for US clients", to: "/virtual-assistants-for-us-clients" },
  { label: "VAs for UK clients", to: "/va-for-uk" },
  { label: "VAs for Canada", to: "/va-for-canada" },
  { label: "VA House vs Upwork", to: "/vs-upwork" },
];

export const tierOptions = pricingTiers.map((tier) => ({
  value: tier.id,
  label: tier.name,
}));

/** Optional role interest on contact form */
export const serviceRoleOptions = serviceRoles.map((role) => ({
  value: role.id,
  label: role.title,
}));
